import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { flushSync } from "react-dom";

const THEME_KEY = "em-theme";
const THEME_COLORS = { dark: "#0a0a0b", light: "#fafaf8" };

const ThemeContext = createContext({
    theme: "dark",
    toggleTheme: () => {},
});

function getInitialTheme() {
    if (typeof document === "undefined") return "dark";
    return document.documentElement.classList.contains("dark")
        ? "dark"
        : "light";
}

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(getInitialTheme);

    useEffect(() => {
        const root = document.documentElement;
        root.classList.toggle("dark", theme === "dark");
        try {
            localStorage.setItem(THEME_KEY, theme);
        } catch {
            /* almacenamiento no disponible */
        }
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute("content", THEME_COLORS[theme]);
    }, [theme]);

    // Cambia el tema con un reveal circular que nace en el punto del clic
    // (View Transitions API). Si el navegador no la soporta o el usuario
    // prefiere menos movimiento, cambia sin animación.
    const toggleTheme = useCallback(
        (event) => {
            const next = theme === "dark" ? "light" : "dark";
            const reduceMotion = window.matchMedia(
                "(prefers-reduced-motion: reduce)",
            ).matches;

            if (!document.startViewTransition || reduceMotion) {
                setTheme(next);
                return;
            }

            const x = event?.clientX ?? window.innerWidth - 48;
            const y = event?.clientY ?? 48;
            const radius = Math.hypot(
                Math.max(x, window.innerWidth - x),
                Math.max(y, window.innerHeight - y),
            );

            const transition = document.startViewTransition(() => {
                flushSync(() => setTheme(next));
            });

            transition.ready.then(() => {
                document.documentElement.animate(
                    {
                        clipPath: [
                            `circle(0px at ${x}px ${y}px)`,
                            `circle(${radius}px at ${x}px ${y}px)`,
                        ],
                    },
                    {
                        duration: 650,
                        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
                        pseudoElement: "::view-transition-new(root)",
                    },
                );
            });
        },
        [theme],
    );

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
